/**
 * Account Linking Utilities
 *
 * Firebase has "One account per email address" enabled project-wide, so a user
 * who registered with email/password gets `auth/account-exists-with-different-credential`
 * when they try "Continue with Google" using the same email. We capture the
 * Google credential from that error, ask the user to confirm with their
 * password, and then attach the credential so either method works next time.
 */

import { type AuthCredential, linkWithCredential, type User } from 'firebase/auth';

/** Firebase Auth error codes we branch on. */
export const FIREBASE_AUTH_ERROR = {
  ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL: 'auth/account-exists-with-different-credential',
  CREDENTIAL_ALREADY_IN_USE: 'auth/credential-already-in-use',
  MFA_REQUIRED: 'auth/multi-factor-auth-required',
  INVALID_CREDENTIAL: 'auth/invalid-credential',
  TOO_MANY_REQUESTS: 'auth/too-many-requests',
} as const;

/** Result of a link attempt: success, or failure with the i18n key to surface. */
export type LinkResult = { success: true } | { success: false; i18nKey: string };

/**
 * Attach `credential` to `user`. Link failures are non-fatal — the user is
 * already authenticated, we just couldn't save them from having to do this
 * again. Returns a `LinkResult`: `{ success: true }` on success, or
 * `{ success: false, i18nKey }` when linking fails so the caller can
 * surface the translated message consistently.
 */
export async function linkPendingCredential(user: User, credential: AuthCredential): Promise<LinkResult> {
  try {
    await linkWithCredential(user, credential);
    return { success: true };
  } catch (err) {
    const code = (err as { code?: string }).code;
    console.error('[account-linking] link failed:', err);
    if (code === FIREBASE_AUTH_ERROR.CREDENTIAL_ALREADY_IN_USE) {
      return { success: false, i18nKey: 'auth.googleCredentialInUse' };
    }
    return { success: false, i18nKey: 'auth.googleLinkFailed' };
  }
}
